import { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useScheduleLogsStorage } from './useScheduleLogsStorage';

interface ScheduleChange {
  id: string;
  schedule_id: string | null;
  action_type: 'INSERT' | 'UPDATE' | 'DELETE';
  action_by: string | null;
  action_timestamp: string;
  field_changed: string | null;
  old_value: string | null;
  new_value: string | null;
  shift_title: string | null;
  shift_description: string | null;
  shift_start_date: string | null;
  shift_end_date: string | null;
  shift_location: string | null;
  team_name: string | null;
  assigned_guard_names: string[] | null;
  change_reason: string | null;
  duration_hours: number | null;
  action_by_name: string | null;
}

interface UseOptimizedScheduleLogsProps {
  filterType: string;
  searchTerm: string;
  selectedTeam: string;
  selectedGuard: string;
  selectedAdmin: string;
  selectedDate?: Date;
}

export const useOptimizedScheduleLogs = (props: UseOptimizedScheduleLogsProps) => {
  const { cachedLogs, lastSync, updateCache, mergeWithCache } = useScheduleLogsStorage();
  const [isInitialLoad, setIsInitialLoad] = useState(true);

  // Fetch new changes since last sync using the shift_changes table directly
  const { data: newChanges, isLoading: isLoadingNew } = useQuery({
    queryKey: ['schedule-audit-new', lastSync],
    queryFn: async () => {
      console.log('Fetching new shift changes data...');
      
      let query = supabase
        .from('shift_changes')
        .select(`
          id,
          schedule_id,
          change_type,
          created_by,
          created_at,
          previous_data,
          new_data,
          team_id,
          guard_id,
          teams(name)
        `)
        .order('created_at', { ascending: false })
        .limit(100);

      // Filter by last sync time if available
      if (lastSync) {
        query = query.gt('created_at', lastSync);
      }

      const { data, error } = await query;

      console.log('Shift changes result:', { data, error });
      if (error) {
        console.error('Shift changes error:', error);
        throw error;
      }
      
      // Transform data to match expected format
      const transformedData = (data || []).map(item => {
        const newData = item.new_data as any;
        const previousData = item.previous_data as any;
        
        // Extract meaningful field changes
        let fieldChanged = 'schedule';
        let oldValue = null;
        let newValue = null;
        
        if (previousData && newData) {
          // Check which specific field changed
          if (previousData.start_date !== newData.start_date) {
            fieldChanged = 'start_time';
            oldValue = previousData.start_date;
            newValue = newData.start_date;
          } else if (previousData.end_date !== newData.end_date) {
            fieldChanged = 'end_time';
            oldValue = previousData.end_date;
            newValue = newData.end_date;
          } else if (previousData.title !== newData.title) {
            fieldChanged = 'title';
            oldValue = previousData.title;
            newValue = newData.title;
          } else if (previousData.description !== newData.description) {
            fieldChanged = 'description';
            oldValue = previousData.description;
            newValue = newData.description;
          } else if (JSON.stringify(previousData.assigned_guards) !== JSON.stringify(newData.assigned_guards)) {
            fieldChanged = 'assigned_guards';
            // Store the actual guard IDs for later name resolution
            oldValue = JSON.stringify(previousData.assigned_guards || []);
            newValue = JSON.stringify(newData.assigned_guards || []);
          }
        }

        // Get guard names from current data (prefer new_data, fallback to previous_data)
        const assignedGuardIds = newData?.assigned_guards || previousData?.assigned_guards || [];
        
        return {
          id: item.id,
          schedule_id: item.schedule_id,
          action_type: item.change_type === 'added' ? 'INSERT' : 
                      item.change_type === 'removed' ? 'DELETE' : 
                      item.change_type === 'assignment_added' ? 'INSERT' :
                      item.change_type === 'assignment_removed' ? 'DELETE' : 'UPDATE',
          action_by: item.created_by,
          action_timestamp: item.created_at,
          field_changed: fieldChanged,
          old_value: oldValue,
          new_value: newValue,
          shift_title: newData?.shift_title || newData?.title || previousData?.shift_title || previousData?.title || null,
          shift_description: newData?.description || previousData?.description || null,
          shift_start_date: newData?.start_date || previousData?.start_date || null,
          shift_end_date: newData?.end_date || previousData?.end_date || null,
          shift_location: newData?.location || previousData?.location || null,
          team_name: item.teams?.name || null,
          assigned_guard_names: Array.isArray(assignedGuardIds) ? assignedGuardIds : [],
          change_reason: item.change_type === 'assignment_added' ? 'Guard assigned to shift' :
                        item.change_type === 'assignment_removed' ? 'Guard removed from shift' :
                        `Schedule ${item.change_type}`,
          duration_hours: null,
          action_by_name: 'System User' // Will be resolved later
        };
      });
      
      console.log('Transformed shift changes:', transformedData.length, 'records');
      return transformedData as ScheduleChange[];
    },
    enabled: true,
    staleTime: 30 * 1000, // 30 seconds
  });

  // Fetch all changes for initial load or when no cache exists
  const { data: allChanges, isLoading: isLoadingAll } = useQuery({
    queryKey: ['schedule-audit-all-initial'],
    queryFn: async () => {
      console.log('Fetching all shift changes for initial load...');
      
      const { data, error } = await supabase
        .from('shift_changes')
        .select(`
          id,
          schedule_id,
          change_type,
          created_by,
          created_at,
          previous_data,
          new_data,
          team_id,
          guard_id,
          teams(name)
        `)
        .order('created_at', { ascending: false })
        .limit(500);

      console.log('Initial shift changes result:', { data, error });
      if (error) {
        console.error('Initial shift changes error:', error);
        throw error;
      }
      
      // Transform data to match expected format
      const transformedData = (data || []).map(item => {
        const newData = item.new_data as any;
        const previousData = item.previous_data as any;
        
        // Extract meaningful field changes
        let fieldChanged = 'schedule';
        let oldValue = null;
        let newValue = null;
        
        if (previousData && newData) {
          // Check which specific field changed
          if (previousData.start_date !== newData.start_date) {
            fieldChanged = 'start_time';
            oldValue = previousData.start_date;
            newValue = newData.start_date;
          } else if (previousData.end_date !== newData.end_date) {
            fieldChanged = 'end_time';
            oldValue = previousData.end_date;
            newValue = newData.end_date;
          } else if (previousData.title !== newData.title) {
            fieldChanged = 'title';
            oldValue = previousData.title;
            newValue = newData.title;
          } else if (previousData.description !== newData.description) {
            fieldChanged = 'description';
            oldValue = previousData.description;
            newValue = newData.description;
          } else if (JSON.stringify(previousData.assigned_guards) !== JSON.stringify(newData.assigned_guards)) {
            fieldChanged = 'assigned_guards';
            // Store the actual guard IDs for later name resolution
            oldValue = JSON.stringify(previousData.assigned_guards || []);
            newValue = JSON.stringify(newData.assigned_guards || []);
          }
        }
        
        // Get guard names from current data (prefer new_data, fallback to previous_data)
        const assignedGuardIds = newData?.assigned_guards || previousData?.assigned_guards || [];
        
        return {
          id: item.id,
          schedule_id: item.schedule_id,
          action_type: item.change_type === 'added' ? 'INSERT' : 
                      item.change_type === 'removed' ? 'DELETE' : 
                      item.change_type === 'assignment_added' ? 'INSERT' :
                      item.change_type === 'assignment_removed' ? 'DELETE' : 'UPDATE',
          action_by: item.created_by,
          action_timestamp: item.created_at,
          field_changed: fieldChanged,
          old_value: oldValue,
          new_value: newValue,
          shift_title: newData?.shift_title || newData?.title || previousData?.shift_title || previousData?.title || null,
          shift_description: newData?.description || previousData?.description || null,
          shift_start_date: newData?.start_date || previousData?.start_date || null,
          shift_end_date: newData?.end_date || previousData?.end_date || null,
          shift_location: newData?.location || previousData?.location || null,
          team_name: item.teams?.name || null,
          assigned_guard_names: Array.isArray(assignedGuardIds) ? assignedGuardIds : [],
          change_reason: item.change_type === 'assignment_added' ? 'Guard assigned to shift' :
                        item.change_type === 'assignment_removed' ? 'Guard removed from shift' :
                        `Schedule ${item.change_type}`,
          duration_hours: null,
          action_by_name: 'System User' // Will be resolved later
        };
      });
      
      console.log('Initial transformed shift changes:', transformedData.length, 'records');
      return transformedData as ScheduleChange[];
    },
    enabled: !lastSync || cachedLogs.length === 0,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  // Merge data when new changes arrive
  useEffect(() => {
    if (newChanges && newChanges.length > 0) {
      mergeWithCache(newChanges);
    }
  }, [newChanges, mergeWithCache]);

  // Update cache when all changes are loaded initially
  useEffect(() => {
    if (allChanges && allChanges.length > 0 && (!lastSync || cachedLogs.length === 0)) {
      updateCache(allChanges);
      setIsInitialLoad(false);
    }
  }, [allChanges, updateCache, lastSync, cachedLogs.length]);

  // Set initial load to false when using cached data
  useEffect(() => {
    if (cachedLogs.length > 0 && isInitialLoad) {
      setIsInitialLoad(false);
    }
  }, [cachedLogs.length, isInitialLoad]);

  // Resolve guard names and user names for cached logs
  useEffect(() => {
    const resolveNames = async () => {
      if (cachedLogs.length === 0) return;
      
      // Get all unique guard IDs from all logs
      const allGuardIds = new Set<string>();
      const allUserIds = new Set<string>();
      
      cachedLogs.forEach(log => {
        // Collect guard IDs
        if (log.assigned_guard_names && Array.isArray(log.assigned_guard_names)) {
          log.assigned_guard_names.forEach(id => {
            if (typeof id === 'string' && id.length > 0) {
              // Check if it looks like a UUID (guard ID) - only collect IDs that haven't been resolved yet
              const isUUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(id);
              if (isUUID) {
                allGuardIds.add(id);
              }
            }
          });
        }
        
        // Collect user IDs who made changes
        if (log.action_by && typeof log.action_by === 'string') {
          allUserIds.add(log.action_by);
        }
      });

      // Fetch guard names and user names
      const profilePromises = [];
      
      // Also collect any guard IDs that might be in old_value/new_value for assigned_guards changes
      const guardChangeIds = new Set<string>();
      cachedLogs.forEach(log => {
        if (log.field_changed === 'assigned_guards') {
          // Try to extract guard IDs from old_value and new_value
          [log.old_value, log.new_value].forEach(value => {
            if (value && typeof value === 'string') {
              // Check if it's a UUID
              const uuidRegex = /[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}/gi;
              const matches = value.match(uuidRegex);
              if (matches) {
                matches.forEach(id => guardChangeIds.add(id));
              }
            }
          });
        }
      });

      // Combine all guard IDs
      guardChangeIds.forEach(id => allGuardIds.add(id));
      
      if (allGuardIds.size > 0) {
        profilePromises.push(
          supabase
            .from('profiles')
            .select('id, full_name, first_name, last_name')
            .in('id', Array.from(allGuardIds))
        );
      }
      
      if (allUserIds.size > 0) {
        profilePromises.push(
          supabase
            .from('profiles')
            .select('id, full_name, first_name, last_name')
            .in('id', Array.from(allUserIds))
        );
      }

      if (profilePromises.length === 0) return;

      const results = await Promise.all(profilePromises);
      
      // Combine all profile data
      const allProfiles = results.reduce((acc, result) => {
        if (result.data) {
          acc.push(...result.data);
        }
        return acc;
      }, [] as any[]);

      // Create name mappings
      const nameMap = new Map<string, string>();
      allProfiles.forEach(profile => {
        const name = profile.full_name?.trim() || 
                    (profile.first_name && profile.last_name ? `${profile.first_name} ${profile.last_name}`.trim() : '') ||
                    'Unknown User';
        nameMap.set(profile.id, name);
      });

      // Update cached logs with resolved names
      const updatedLogs = cachedLogs.map(log => {
        const updatedLog = {
          ...log,
          assigned_guard_names: log.assigned_guard_names?.map(id => 
            typeof id === 'string' && nameMap.has(id) ? nameMap.get(id)! : id
          ) || [],
          action_by_name: log.action_by && nameMap.has(log.action_by) ? nameMap.get(log.action_by)! : 'System User'
        };

        // If the field changed was assigned_guards, resolve the old_value and new_value
        if (updatedLog.field_changed === 'assigned_guards') {
          // Handle old_value - check if it contains UUIDs and replace them with names
          if (updatedLog.old_value) {
            let resolvedOldValue = updatedLog.old_value;
            const uuidRegex = /[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}/gi;
            const oldMatches = updatedLog.old_value.match(uuidRegex);
            if (oldMatches) {
              oldMatches.forEach(uuid => {
                const name = nameMap.get(uuid);
                if (name) {
                  resolvedOldValue = resolvedOldValue.replace(uuid, name);
                }
              });
              updatedLog.old_value = resolvedOldValue;
            }
          }
          
          // Handle new_value - check if it contains UUIDs and replace them with names  
          if (updatedLog.new_value) {
            let resolvedNewValue = updatedLog.new_value;
            const uuidRegex = /[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}/gi;
            const newMatches = updatedLog.new_value.match(uuidRegex);
            if (newMatches) {
              newMatches.forEach(uuid => {
                const name = nameMap.get(uuid);
                if (name) {
                  resolvedNewValue = resolvedNewValue.replace(uuid, name);
                }
              });
              updatedLog.new_value = resolvedNewValue;
            }
          }
        }

        return updatedLog;
      });

      // Only update if names have changed
      const hasChanges = cachedLogs.some((log, index) => 
        JSON.stringify(log.assigned_guard_names) !== JSON.stringify(updatedLogs[index].assigned_guard_names) ||
        log.action_by_name !== updatedLogs[index].action_by_name
      );

      if (hasChanges) {
        updateCache(updatedLogs);
      }
    };

    resolveNames();
  }, [cachedLogs.length, updateCache]);

  // Filter cached logs based on criteria and sort by timestamp descending
  const filteredChanges = cachedLogs
    .filter(change => {
    // Type filter - map UI types to database types
    if (props.filterType !== "all") {
      const typeMapping: { [key: string]: string } = {
        'added': 'INSERT',
        'removed': 'DELETE', 
        'modified': 'UPDATE'
      };
      const mappedType = typeMapping[props.filterType] || props.filterType;
      if (change.action_type !== mappedType) {
        return false;
      }
    }

    // Team filter - check both team ID and team name
    if (props.selectedTeam !== "all") {
      // First check if it's a team ID (UUID format)
      const isTeamId = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(props.selectedTeam);
      
      if (isTeamId) {
        // Compare with team ID if we have access to it (from the raw data)
        // Since we don't have team_id in the transformed data, we'll use team name matching
        if (change.team_name !== props.selectedTeam) {
          return false;
        }
      } else {
        // Compare with team name
        if (change.team_name !== props.selectedTeam) {
          return false;
        }
      }
    }

    // Guard filter - check if guard name or ID is in assigned guards
    if (props.selectedGuard !== "all") {
      const guardNames = change.assigned_guard_names || [];
      const isGuardId = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(props.selectedGuard);
      
      if (isGuardId) {
        // We need to check if this guard ID was involved in this change
        // Since the assigned_guard_names might contain resolved names, we need to check differently
        // For now, we'll check if the selectedGuard matches any name or if it's an ID that was resolved
        const foundGuard = guardNames.some(name => {
          // Check if name matches exactly or if it could be an unresolved ID
          return name === props.selectedGuard || 
                 (typeof name === 'string' && name.includes(props.selectedGuard));
        });
        if (!foundGuard) {
          return false;
        }
      } else {
        // Compare with resolved guard names
        if (!guardNames.some(name => name === props.selectedGuard)) {
          return false;
        }
      }
    }

    // Admin filter - check action by ID or resolved name
    if (props.selectedAdmin !== "all") {
      const isAdminId = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(props.selectedAdmin);
      
      if (isAdminId) {
        // Compare with action_by (user ID)
        if (change.action_by !== props.selectedAdmin) {
          return false;
        }
      } else {
        // Compare with resolved admin name
        if (change.action_by_name !== props.selectedAdmin) {
          return false;
        }
      }
    }

    // Date filter
    if (props.selectedDate) {
      const changeDate = new Date(change.action_timestamp);
      const selectedDate = new Date(props.selectedDate);
      
      const startOfDay = new Date(selectedDate);
      startOfDay.setHours(0, 0, 0, 0);
      const endOfDay = new Date(selectedDate);
      endOfDay.setHours(23, 59, 59, 999);
      
      if (changeDate < startOfDay || changeDate > endOfDay) {
        return false;
      }
    }

    // Search filter
    if (props.searchTerm) {
      const guardNames = (change.assigned_guard_names || []).join(' ');
      const teamName = change.team_name || '';
      const creatorName = change.action_by_name || '';
      const shiftTitle = change.shift_title || '';
      const location = change.shift_location || '';
      
      const searchTermLower = props.searchTerm.toLowerCase();
      return (
        guardNames.toLowerCase().includes(searchTermLower) ||
        teamName.toLowerCase().includes(searchTermLower) ||
        creatorName.toLowerCase().includes(searchTermLower) ||
        shiftTitle.toLowerCase().includes(searchTermLower) ||
        location.toLowerCase().includes(searchTermLower)
      );
    }

    return true;
  })
  .sort((a, b) => new Date(b.action_timestamp).getTime() - new Date(a.action_timestamp).getTime());

  const isLoading = isInitialLoad && (isLoadingAll || (isLoadingNew && cachedLogs.length === 0));

  return {
    changes: filteredChanges,
    isLoading,
    lastSync,
    totalCached: cachedLogs.length,
    hasNewData: newChanges && newChanges.length > 0
  };
};